import { type MouseEvent, type ReactElement, type ReactNode, useState } from 'react';
import { ProjectBadge } from '@/features/projects/components/ProjectBadge';
import {
  buildProjectScopedPath,
  isProjectScopedProductPath,
  stripProjectRouteScope,
} from '@/features/projects/utils/projectScopedRoutes';
import { buildProjectSettingsHrefForProject } from '@/features/projects/utils/projectSettingsRoute';
import { getFilterEmptyLabel } from '@/shared/copy/filters';
import { getToastMessage } from '@/shared/copy/toasts';
import { s, typographyTokens } from '@/theme/tokens';
import { Box, Text } from '@/ui';
import { layoutModeTokens, useLayoutMode } from '@/ui/layouts/layoutMode';
import {
  AddIcon,
  BusinessIcon,
  Divider,
  ExpandIcon,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  WorkOutline,
} from '@/ui/primitives';
import type { NotificationContextValue } from '@/providers/notificationContext';
import type { OrganisationDto, ProjectDto } from '@flowqa/shared';

type ProjectOption = Pick<
  ProjectDto,
  'id' | 'name' | 'slug' | 'organisationId' | 'badgeChars' | 'badgeColor' | 'badgeEnabled'
> & {
  locked: boolean;
  lockReason: string | null;
};

type TopBarContextControlsProps = {
  activePath: string;
  organisationLabel: string;
  selectedOrganisationId: string | null;
  organisations: Array<Pick<OrganisationDto, 'id' | 'name' | 'slug'>>;
  projectLabel: string;
  selectedProjectId: string | null;
  projects: ProjectOption[];
  canCreateProject: boolean;
  lockProjectSelector: boolean;
  notify: NotificationContextValue['notify'];
  onNavigate: (path: string) => void;
  onAddProject: () => void;
  setSelectedOrganisationId: (value: string | null) => void;
  setSelectedProjectId: (value: string | null) => void;
};

type ActiveMenu = 'organisation' | 'project' | null;

const contextButtonHeight = 34;
const projectMenuItemHeight = 42;

const contextIconSx = {
  fontSize: 20,
  stroke: 'currentColor',
  strokeWidth: 0.08,
} as const;

const projectIconFrameSx = {
  width: 20,
  height: 20,
  borderRadius: s('xs'),
  backgroundColor: 'transparent',
  color: 'inherit',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
} as const;

const isProjectSettingsPath = (activePath: string): boolean =>
  activePath === '/project-settings' ||
  /^\/workspaces\/[^/]+\/projects\/[^/]+\/settings$/.test(activePath);

const resolveProjectSwitchPath = ({
  activePath,
  organisations,
  project,
}: {
  activePath: string;
  organisations: TopBarContextControlsProps['organisations'];
  project: ProjectOption;
}): string => {
  const workspaceSlug =
    project.organisationId === null
      ? null
      : (organisations.find((organisation) => organisation.id === project.organisationId)?.slug ??
        null);

  if (isProjectSettingsPath(activePath)) {
    return buildProjectSettingsHrefForProject({
      projectId: project.id,
      projectSlug: project.slug,
      workspaceSlug,
    });
  }
  if (workspaceSlug !== null && isProjectScopedProductPath(activePath)) {
    return buildProjectScopedPath({
      workspaceSlug,
      projectSlug: project.slug,
      path: stripProjectRouteScope(activePath),
    });
  }
  return activePath;
};

const resolveAllProjectsSwitchPath = (activePath: string): string => {
  if (isProjectSettingsPath(activePath)) {
    return '/project-settings';
  }
  return '/execution-runs';
};

export const TopBarContextControls = ({
  activePath,
  organisationLabel,
  selectedOrganisationId,
  organisations,
  projectLabel,
  selectedProjectId,
  projects,
  canCreateProject,
  lockProjectSelector,
  notify,
  onNavigate,
  onAddProject,
  setSelectedOrganisationId,
  setSelectedProjectId,
}: TopBarContextControlsProps): ReactElement => {
  const layoutMode = useLayoutMode();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeMenu, setActiveMenu] = useState<ActiveMenu>(null);
  const useCompactContextControl = layoutMode !== layoutModeTokens.wide;
  const canOpenOrganisationMenu = organisations.length > 1;
  const canOpenProjectMenu = !lockProjectSelector;
  const projectItems = projects.length > 0 ? projects : [];
  const showAllProjectsOption = projectItems.length > 1;
  const selectedProject =
    selectedProjectId === null
      ? null
      : (projects.find((project) => project.id === selectedProjectId) ?? null);
  const selectedProjectBadge =
    selectedProject?.badgeEnabled === true
      ? {
          chars: selectedProject.badgeChars,
          color: selectedProject.badgeColor,
        }
      : null;
  const projectIcon =
    selectedProjectBadge === null ? (
      <Box component="span" sx={projectIconFrameSx}>
        <WorkOutline sx={{ fontSize: 17, stroke: 'currentColor', strokeWidth: 0.08 }} />
      </Box>
    ) : (
      <ProjectBadge chars={selectedProjectBadge.chars} color={selectedProjectBadge.color} />
    );
  const showProjectMenuItems = activeMenu === 'project' && canOpenProjectMenu;

  const openMenu = (event: MouseEvent<HTMLElement>, menu: Exclude<ActiveMenu, null>): void => {
    setAnchorEl(event.currentTarget);
    setActiveMenu(menu);
  };

  const closeMenu = (): void => {
    setAnchorEl(null);
    setActiveMenu(null);
  };

  const renderContextButton = ({
    label,
    icon,
    disabled,
    showChevron = true,
    onClick,
  }: {
    label: ReactNode;
    icon: ReactElement | null;
    disabled: boolean;
    showChevron?: boolean;
    onClick: (event: MouseEvent<HTMLElement>) => void;
  }): ReactElement => (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={onClick}
      sx={(theme) => ({
        appearance: 'none',
        border: 'none',
        backgroundColor: 'transparent',
        color: theme.app.colors.textMuted,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: useCompactContextControl ? 'center' : 'flex-start',
        gap: s('md'),
        height: contextButtonHeight,
        minWidth: 0,
        width: 'auto',
        maxWidth: 260,
        px: s('none'),
        borderRadius: s('sm'),
        cursor: disabled ? 'default' : 'pointer',
        font: 'inherit',
        flexShrink: 1,
        '&:hover': { backgroundColor: 'transparent' },
        '&:focus-visible': {
          outline: `2px solid ${theme.app.state.focus}`,
          outlineOffset: 2,
        },
      })}
    >
      <Box
        component="span"
        sx={(theme) => ({
          color: theme.app.colors.textMuted,
          display: icon === null ? 'none' : 'inline-flex',
          alignItems: 'center',
          flexShrink: 0,
        })}
      >
        {icon}
      </Box>
      <Text
        role="bodyStrong"
        component="span"
        sx={{
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'block',
          lineHeight: 1,
          fontSize: 15.5,
          fontWeight: typographyTokens.overline.fontWeight,
          color: (theme) => theme.app.colors.textMuted,
        }}
      >
        {label}
      </Text>
      {showChevron ? (
        <ExpandIcon
          sx={{
            fontSize: 17,
            flexShrink: 0,
            color: (theme) => theme.app.colors.textMuted,
            ml: s('xxs'),
          }}
        />
      ) : null}
    </Box>
  );

  const renderOrganisationItems = (): ReactElement[] =>
    organisations.map((organisation) => (
      <MenuItem
        key={organisation.id}
        selected={organisation.id === selectedOrganisationId}
        onClick={() => {
          setSelectedOrganisationId(organisation.id);
          setSelectedProjectId(null);
          notify({
            message: getToastMessage('context.workspaceSwitched', {
              name: organisation.name,
            }),
            tone: 'blue',
          });
          closeMenu();
          onNavigate('/execution-runs');
        }}
      >
        <ListItemIcon>
          <BusinessIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary={organisation.name} />
      </MenuItem>
    ));

  const renderProjectItems = (): ReactElement[] =>
    projectItems.map((project) => {
      const projectBadge = project.badgeEnabled ? (
        <ProjectBadge chars={project.badgeChars} color={project.badgeColor} />
      ) : null;

      return (
        <MenuItem
          key={project.id}
          onClick={() => {
            setSelectedProjectId(project.id);
            notify({
              message: project.locked
                ? getToastMessage('context.projectViewOnly', { name: project.name })
                : getToastMessage('context.projectSwitched', { name: project.name }),
              tone: project.locked ? 'orange' : 'blue',
            });
            closeMenu();
            onNavigate(resolveProjectSwitchPath({ activePath, organisations, project }));
          }}
        >
          {projectBadge === null ? null : (
            <Box component="span" sx={{ flexShrink: 0, mr: s('md') }}>
              {projectBadge}
            </Box>
          )}
          <ListItemText
            primary={project.name}
            secondary={project.lockReason ?? undefined}
            sx={{ minWidth: 0 }}
            slotProps={{
              secondary: {
                sx: { fontSize: (theme) => theme.typography.caption.fontSize },
              },
            }}
          />
        </MenuItem>
      );
    });

  return (
    <>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: s('md'),
          justifyContent: useCompactContextControl ? 'center' : 'flex-start',
          minWidth: 0,
          maxWidth: '100%',
          width: '100%',
        }}
      >
        {renderContextButton({
          label: organisationLabel,
          icon: useCompactContextControl ? null : <BusinessIcon sx={contextIconSx} />,
          disabled: !canOpenOrganisationMenu,
          showChevron: canOpenOrganisationMenu,
          onClick: (event) => {
            openMenu(event, 'organisation');
          },
        })}
        <Text
          role="body"
          component="span"
          sx={{
            fontSize: 17,
            color: (theme) => theme.app.colors.textMuted,
            mx: s('sm'),
            flexShrink: 0,
          }}
        >
          /
        </Text>
        {renderContextButton({
          label: projectLabel,
          icon: projectIcon,
          disabled: !canOpenProjectMenu,
          onClick: (event) => {
            openMenu(event, 'project');
          },
        })}
      </Box>
      <Popover
        open={Boolean(anchorEl && activeMenu)}
        anchorEl={anchorEl}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        paperSx={(theme) => ({
          mt: s('xs'),
          width: 'max-content',
          minWidth: 0,
          border: `1px solid ${theme.app.colors.border}`,
          borderRadius: '4px !important',
          backgroundColor: theme.app.colors.surface,
          backgroundImage: 'none',
          boxShadow: 'none',
          overflow: 'hidden',
        })}
      >
        {activeMenu === 'organisation' ? (
          <MenuList
            dense
            disablePadding
            sx={{
              '& .MuiListItemText-primary': {
                fontWeight: typographyTokens.overline.fontWeight,
              },
            }}
          >
            {renderOrganisationItems()}
          </MenuList>
        ) : null}
        {activeMenu === 'project' ? (
          <MenuList
            dense
            disablePadding
            sx={{
              py: s('none'),
              '& .MuiMenuItem-root': {
                minHeight: `${projectMenuItemHeight}px`,
                px: s('xl'),
                py: s('sm'),
              },
              '& .MuiListItemText-primary': {
                fontWeight: typographyTokens.overline.fontWeight,
              },
              '& .MuiListItemText-secondary': {
                fontWeight: typographyTokens.body.fontWeight,
              },
              '& .MuiDivider-root': {
                my: `${s('none')} !important`,
                marginTop: `${s('none')} !important`,
                marginBottom: `${s('none')} !important`,
              },
            }}
          >
            {showProjectMenuItems && showAllProjectsOption ? (
              <MenuItem
                onClick={() => {
                  setSelectedProjectId(null);
                  notify({ message: getToastMessage('context.viewingAllProjects'), tone: 'blue' });
                  closeMenu();
                  onNavigate(resolveAllProjectsSwitchPath(activePath));
                }}
              >
                <ListItemText primary={getFilterEmptyLabel('allProjects')} />
              </MenuItem>
            ) : null}
            {showProjectMenuItems ? renderProjectItems() : null}
            {showProjectMenuItems
              ? [
                  <Divider key="project-actions-divider" />,
                  <MenuItem
                    key="add-project"
                    disabled={!canCreateProject}
                    onClick={() => {
                      if (!canCreateProject) return;
                      closeMenu();
                      onAddProject();
                    }}
                  >
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: s('sm') }}>
                      <AddIcon fontSize="small" />
                      <ListItemText primary="Add New" />
                    </Box>
                  </MenuItem>,
                ]
              : null}
          </MenuList>
        ) : null}
      </Popover>
    </>
  );
};
